(() => {
  const SELECTOR = '[data-vc-bundle-builder]';

  class VelvetBundleBuilder {
    constructor(root) {
      if (root.dataset.vcBundleInitialised === 'true') return;
      root.dataset.vcBundleInitialised = 'true';

      this.root = root;
      this.items = [...root.querySelectorAll('[data-vc-bundle-item]')];
      this.inputs = [...root.querySelectorAll('[data-vc-bundle-choice]')];
      this.form = document.getElementById(root.dataset.productFormId);
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.submitButton = this.form?.querySelector('button[name="add"]');
      this.submitText = this.submitButton?.querySelector('span');
      this.message = root.querySelector('[data-vc-bundle-message]');
      this.progressCount = root.querySelector('[data-vc-bundle-progress-count]');
      this.progressBar = root.querySelector('[data-vc-bundle-progress-bar]');
      this.itemCount = Number(root.dataset.itemCount || this.items.length);
      this.optionCount = Number(root.dataset.optionCount || 0);
      this.variants = this.readVariants();
      this.ready = false;

      if (!this.form || !this.variantInput || !this.submitButton || !this.items.length) return;

      this.inputs.forEach((input) => input.addEventListener('change', () => this.handleChoice(input)));
      this.markUnavailableChoices();
      this.items.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          this.items.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
      this.form.addEventListener('submit', (event) => this.guardSubmit(event), true);

      this.update();
    }

    readVariants() {
      try {
        return JSON.parse(this.root.querySelector('[data-vc-bundle-variants]')?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    markUnavailableChoices() {
      this.inputs.forEach((input) => {
        const position = Number(input.dataset.vcOptionPosition || 0);
        if (!position) return;

        const canBeBought = this.variants.some(
          (variant) => variant.available && variant.options?.[position - 1] === input.value
        );
        if (canBeBought) return;

        input.disabled = true;
        const label = this.root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return;
        label.setAttribute('aria-disabled', 'true');
        if (!label.querySelector('.vc-bundle-choice__availability')) {
          const badge = document.createElement('small');
          badge.className = 'vc-bundle-choice__availability';
          badge.textContent = 'Sold out';
          label.appendChild(badge);
        }
      });
    }

    handleChoice(input) {
      const item = input.closest('[data-vc-bundle-item]');
      const preview = item?.querySelector('.vc-bundle-preview img');
      const status = item?.querySelector('[data-vc-bundle-item-status]');

      if (preview && input.dataset.vcPreviewSrc) {
        preview.style.opacity = '0.45';
        preview.src = input.dataset.vcPreviewSrc;
        preview.srcset = '';
        preview.alt = input.dataset.vcPreviewAlt || `${item.dataset.vcItemTitle} in ${input.value}`;
        const finish = () => {
          preview.style.opacity = '';
          preview.removeEventListener('load', finish);
        };
        preview.addEventListener('load', finish);
      }

      if (status) status.textContent = input.value;
      if (item) item.dataset.complete = 'true';

      this.update();

      const nextIncomplete = this.items.find((candidate) => !candidate.querySelector('[data-vc-bundle-choice]:checked'));
      if (nextIncomplete && nextIncomplete !== item) {
        window.setTimeout(() => {
          item.open = false;
          nextIncomplete.open = true;
        }, 180);
      }
    }

    selectedVariant() {
      const selectedOptions = [];
      for (let position = 1; position <= this.optionCount; position += 1) {
        const selected = this.root.querySelector(`[data-vc-option-position="${position}"]:checked`);
        if (!selected) return null;
        selectedOptions.push(selected.value);
      }

      return this.variants.find((variant) =>
        selectedOptions.every((value, index) => variant.options?.[index] === value)
      );
    }

    update() {
      const completeItems = this.items.filter((item) => item.querySelector('[data-vc-bundle-choice]:checked')).length;
      const remaining = Math.max(this.itemCount - completeItems, 0);
      const variant = this.selectedVariant();
      const allItemsComplete = completeItems === this.itemCount;
      const variantReady = Boolean(variant?.available);

      if (this.progressCount) this.progressCount.textContent = String(completeItems);
      if (this.progressBar) this.progressBar.style.transform = `scaleX(${this.itemCount ? completeItems / this.itemCount : 0})`;

      if (variant) {
        this.variantInput.disabled = false;
        this.variantInput.value = variant.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.ready = allItemsComplete && variantReady;
      this.root.dataset.ready = String(this.ready);
      this.submitButton.disabled = !this.ready;
      this.submitButton.setAttribute('aria-disabled', String(!this.ready));

      if (this.ready) {
        if (this.submitText) this.submitText.textContent = 'Add bundle to cart';
        if (this.message) this.message.textContent = 'Your bundle is complete. Review the colours above or add it to your cart.';
      } else if (allItemsComplete && !variantReady) {
        if (this.submitText) this.submitText.textContent = 'Combination unavailable';
        if (this.message) this.message.textContent = 'That combination is currently unavailable. Please adjust one of your colours.';
      } else {
        if (this.submitText) this.submitText.textContent = remaining === 1 ? 'Choose 1 more item' : `Choose ${remaining} item colours`;
        if (this.message) this.message.textContent = remaining === 1 ? 'One item still needs a colour.' : `${remaining} items still need a colour.`;
      }
    }

    guardSubmit(event) {
      if (this.ready) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const firstIncomplete = this.items.find((item) => !item.querySelector('[data-vc-bundle-choice]:checked'));
      if (firstIncomplete) {
        firstIncomplete.open = true;
        firstIncomplete.querySelector('summary')?.focus();
      }
      if (this.message) this.message.textContent = 'Please choose one colour for every bundle item before adding it to your cart.';
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => new VelvetBundleBuilder(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
